import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useFetch from "hooks/useFetch";
import {
  Button,
  Card,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Skeleton,
  styles as ui,
  Table,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import styles from "./CachePage.module.scss";

interface CacheEntry {
  cacheName: string;
  keys: string[];
  expanded: boolean;
}

interface CacheData {
  [key: string]: any;
}

const CachePage: React.FC = () => {
  const { execute } = useFetch();
  const confirm = useConfirm();
  const toast = useToast();
  const [cacheKeys, setCacheKeys] = useState<string[]>([]);
  const [cacheEntries, setCacheEntries] = useState<CacheEntry[]>([]);
  const [selectedCacheData, setSelectedCacheData] = useState<CacheData | null>(
    null,
  );
  const [selectedCacheName, setSelectedCacheName] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [isClearing, setIsClearing] = useState(false);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDataModal, setShowDataModal] = useState(false);

  // Helper function to parse cache key string
  // Format: "cacheName: is cached with key KEY" or "cacheName: is cached"
  const parseCacheKey = (
    keyString: string,
  ): { cacheName: string; key?: string } => {
    // Match pattern: "cacheName: is cached with key KEY"
    const withKeyMatch = keyString.match(
      /^(.+?):\s*is cached with key\s+(.+)$/i,
    );
    if (withKeyMatch) {
      return { cacheName: withKeyMatch[1].trim(), key: withKeyMatch[2].trim() };
    }

    // Match pattern: "cacheName: is cached"
    const cachedMatch = keyString.match(/^(.+?):\s*is cached$/i);
    if (cachedMatch) {
      return { cacheName: cachedMatch[1].trim() };
    }

    // Fallback: try splitting by "::" (old format)
    const parts = keyString.split("::");
    if (parts.length > 1) {
      return { cacheName: parts[0], key: parts.slice(1).join("::") };
    }

    // Last resort: return the whole string as cacheName
    return { cacheName: keyString };
  };

  const fetchCacheKeys = async () => {
    setIsLoadingKeys(true);
    try {
      const result = await execute("/api/cached-data/keys", "GET");
      if (result && Array.isArray(result)) {
        setCacheKeys(result);
        // Parse cache keys to group by cache name
        const entriesMap = new Map<string, string[]>();
        result.forEach((keyString: string) => {
          const { cacheName, key } = parseCacheKey(keyString);

          if (!entriesMap.has(cacheName)) {
            entriesMap.set(cacheName, []);
          }
          if (key) {
            entriesMap.get(cacheName)?.push(key);
          }
        });

        const entries: CacheEntry[] = Array.from(entriesMap.entries()).map(
          ([cacheName, keys]) => ({
            cacheName,
            keys,
            expanded: false,
          }),
        );
        setCacheEntries(entries);
      }
    } catch (error) {
      console.error("Feil ved henting av cache-nøkler:", error);
    }
    setIsLoadingKeys(false);
  };

  const fetchCacheData = async (cacheName: string, key?: string) => {
    setIsLoadingData(true);
    setSelectedCacheName(cacheName);
    setSelectedKey(key || "");
    try {
      const params: { cacheName: string; key?: string } = { cacheName };
      if (key) {
        params.key = key;
      }
      const result = await execute(
        "/api/cached-data/data",
        "GET",
        undefined,
        params,
      );
      if (result) {
        setSelectedCacheData(result);
        setShowDataModal(true);
      }
    } catch (error) {
      console.error("Feil ved henting av cache-data:", error);
    }
    setIsLoadingData(false);
  };

  const clearAllCaches = async () => {
    setIsClearing(true);
    try {
      await execute("/api/cached-data/clear-all", "POST");
      setCacheKeys([]);
      setCacheEntries([]);
      setSelectedCacheData(null);
      toast.success("Alle cacher er tømt.");
    } catch (error) {
      console.error("Feil ved tømming av cache:", error);
      toast.error("Kunne ikke tømme cachen. Prøv igjen.");
    }
    setIsClearing(false);
  };

  const handleClearClick = async () => {
    const ok = await confirm({
      title: "Tømme alle cacher?",
      message: (
        <>
          Dette fjerner {cacheKeys.length} cache-oppføringer. Handlingen kan
          ikke angres.
        </>
      ),
      confirmLabel: "Tøm alle",
      tone: "danger",
    });
    if (ok) await clearAllCaches();
  };

  useEffect(() => {
    fetchCacheKeys();
  }, []);

  const filteredKeys = cacheKeys.filter((key) =>
    key.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const dataModal = showDataModal
    ? createPortal(
        <div
          className={ui.overlay}
          style={{ paddingTop: 70 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowDataModal(false);
          }}
        >
          <div
            className={`${ui.modal} ${styles.dataModal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cache-data-title"
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowDataModal(false);
            }}
          >
            <h2 className={ui.mh} id="cache-data-title">
              <span className={ui.mhIcon} aria-hidden="true">
                <Icon name="db" size={18} strokeWidth={2} />
              </span>
              Cache-data
              <IconButton
                label="Lukk"
                icon={<Icon name="x" size={18} strokeWidth={2} />}
                onClick={() => setShowDataModal(false)}
                style={{ marginLeft: "auto" }}
                autoFocus
              />
            </h2>
            <div className={ui.mb}>
              <dl className={styles.dl}>
                <dt>Cache-navn</dt>
                <dd>
                  <code>{selectedCacheName}</code>
                </dd>
                {selectedKey && (
                  <>
                    <dt>Nøkkel</dt>
                    <dd>
                      <code>{selectedKey}</code>
                    </dd>
                  </>
                )}
              </dl>
              {isLoadingData ? (
                <Skeleton height={200} />
              ) : selectedCacheData ? (
                <pre className={styles.pre}>
                  {JSON.stringify(selectedCacheData, null, 2)}
                </pre>
              ) : (
                <span className={ui.help}>Ingen data å vise.</span>
              )}
            </div>
            <div className={ui.mf}>
              <Button
                variant="secondary"
                onClick={() => setShowDataModal(false)}
              >
                Lukk
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <PageHeader
        title="Cache"
        subtitle="Se og tøm cachen til API-et."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={fetchCacheKeys}
              disabled={isLoadingKeys}
              icon={
                isLoadingKeys ? (
                  <span className={ui.spinner} aria-hidden="true" />
                ) : (
                  <Icon name="swap" size={16} strokeWidth={2} />
                )
              }
            >
              Oppdater
            </Button>
            <Button
              variant="danger"
              onClick={handleClearClick}
              disabled={cacheKeys.length === 0}
              loading={isClearing}
              icon={<Icon name="trash" size={16} strokeWidth={2} />}
            >
              {isClearing ? "Tømmer …" : "Tøm alle cacher"}
            </Button>
          </>
        }
      />

      <div className={ui.g4}>
        <div className={styles.stat}>
          <b>{cacheKeys.length.toLocaleString("nb-NO")}</b>
          <span>Cache-nøkler totalt</span>
        </div>
        <div className={styles.stat}>
          <b>{cacheEntries.length.toLocaleString("nb-NO")}</b>
          <span>Cache-typer</span>
        </div>
      </div>

      <div className={styles.search}>
        <Icon
          name="search"
          size={18}
          strokeWidth={2}
          className={styles.searchIcon}
        />
        <TextField
          type="search"
          aria-label="Søk etter cache-nøkkel"
          placeholder="Søk etter cache-nøkkel …"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {isLoadingKeys ? (
        <Card>
          <Skeleton width="25%" height={20} />
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={28} />
          ))}
        </Card>
      ) : cacheKeys.length === 0 ? (
        <EmptyState icon="db" title="Ingen cache-data">
          Det er ingen data i cachen akkurat nå.
        </EmptyState>
      ) : (
        <Card
          title={`Cache-nøkler (${filteredKeys.length.toLocaleString("nb-NO")})`}
        >
          <Table
            rows={filteredKeys}
            rowKey={(_, index) => index}
            caption="Cache-nøkler"
            empty={
              <EmptyState icon="search" title="Ingen treff">
                Ingen cache-nøkler passer søket.
              </EmptyState>
            }
            columns={[
              {
                key: "index",
                header: "#",
                width: 56,
                numeric: true,
                render: (_, index) => (
                  <span className={ui.help}>{index + 1}</span>
                ),
              },
              {
                key: "key",
                header: "Nøkkel",
                render: (keyString) => (
                  <code className={styles.cacheKey}>{keyString}</code>
                ),
              },
              {
                key: "actions",
                header: <span className={ui.srOnly}>Handlinger</span>,
                align: "right",
                render: (keyString) => {
                  const { cacheName, key: keyPart } = parseCacheKey(keyString);
                  return (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => fetchCacheData(cacheName, keyPart)}
                      disabled={isLoadingData}
                      icon={<Icon name="search" size={14} strokeWidth={2} />}
                    >
                      Vis data
                    </Button>
                  );
                },
              },
            ]}
          />
        </Card>
      )}

      {dataModal}
    </>
  );
};

export default CachePage;
